/**
 * Hook that returns slide scanner config for the current platform and workflow.
 * Used by the shared SlideImageSection component.
 */
import { getInstructionsPdfUrl } from '@samd/features/help';
import {
  getScannerOptionsForWorkflow,
  getSupportedFileExtensions,
  getSupportedFileTypesText,
  validateScannerFileCompatibility,
} from '@samd/features/orders/utils/slide_scanners';
import { getLdtSlideScannerConfig } from '@ldt/features/orders/utils/slide_scanner_config';
import { getPlatform } from 'src/config/environment';
import { useWorkflowType } from 'src/distribution_platforms/features/hooks/use_workflow';
import type { SlideScannerConfig } from 'src/distribution_platforms/features/types/slide_scanner';
import type { SpecimenType } from 'src/types';
import { DistributionPlatform } from 'src/types/env';

export type { SlideScannerConfig } from 'src/distribution_platforms/features/types/slide_scanner';

export function useSlideScannerConfig(
  specimenType: SpecimenType,
): SlideScannerConfig {
  const workflowType = useWorkflowType();
  const platform = getPlatform();

  if (platform === DistributionPlatform.Ldt) {
    return getLdtSlideScannerConfig(workflowType);
  }

  if (platform === DistributionPlatform.Samd) {
    return {
      instructionsPdfUrl: getInstructionsPdfUrl(workflowType),
      scannerOptions: getScannerOptionsForWorkflow(workflowType, specimenType),
      supportedExtensions: getSupportedFileExtensions(
        workflowType,
        specimenType,
      ),
      supportedFileTypesText: getSupportedFileTypesText(
        workflowType,
        specimenType,
      ),
      validateScannerFileCompatibility: (filename, scannerType) =>
        validateScannerFileCompatibility(
          filename,
          scannerType,
          workflowType,
          specimenType,
        ),
    };
  }

  throw new Error(`Unsupported platform: ${platform}`);
}
